import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Check, Building2, Landmark, FileText, CreditCard, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { n: 1, title: "Business Info", icon: Building2 },
  { n: 2, title: "GST Details", icon: Landmark },
  { n: 3, title: "Invoice Settings", icon: FileText },
  { n: 4, title: "Subscription", icon: CreditCard },
];

const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState({
    name: "", owner_name: "", phone: "", email: "", address: "",
    city: "", state: "Maharashtra", pincode: "", logo_url: "",
    gst_registered: false, gst_number: "", state_code: "27", business_type: "Retail",
    invoice_prefix: "INV", invoice_start_number: 1, invoice_fy_format: true, invoice_terms: "Payment due within 30 days.",
    bank_name: "", account_number: "", ifsc: "", upi_id: "", signature_url: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setCompany({ ...company, ...r.data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveStep = async (nextStep) => {
    setLoading(true);
    try {
      await api.post("/company/onboard", { step, data: company });
      setStep(nextStep);
    } catch { toast.error("Failed to save"); }
    setLoading(false);
  };

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/subscription/start-trial", { plan_code: "trial" });
      await api.post("/company/onboard", { step: 4, data: { ...company, onboarded: true } });
      toast.success("You're all set! Welcome aboard.");
      nav("/app");
    } catch (e) {
      toast.error("Failed to activate trial");
    }
    setLoading(false);
  };

  const skip = () => nav("/app");

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 mb-8 font-heading font-bold">
          <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center text-sm">BP</div>
          <span className="text-lg">TheBillPro</span>
          <button onClick={skip} className="ml-auto text-xs text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">Skip for now</button>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <Progress value={(step / 4) * 100} className="h-1.5" />
          <div className="mt-3 grid grid-cols-4 gap-2">
            {STEPS.map((s) => (
              <div key={s.n} className={`text-center text-xs ${s.n <= step ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                <div className={`w-9 h-9 mx-auto rounded-full grid place-items-center mb-1 border-2 ${s.n < step ? "bg-primary border-primary text-primary-foreground" : s.n === step ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                  {s.n < step ? <Check size={16} /> : <s.icon size={14} />}
                </div>
                {s.title}
              </div>
            ))}
          </div>
        </div>

        <Card className="p-6 sm:p-8" data-testid={`onboarding-step-${step}`}>
          {step === 1 && (
            <Step1 company={company} setCompany={setCompany} />
          )}
          {step === 2 && (
            <Step2 company={company} setCompany={setCompany} />
          )}
          {step === 3 && (
            <Step3 company={company} setCompany={setCompany} />
          )}
          {step === 4 && (
            <Step4 company={company} onFinish={finish} loading={loading} />
          )}

          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
            {step < 4 ? (
              <Button onClick={() => saveStep(step + 1)} disabled={loading} data-testid="onboarding-next">
                Continue <ArrowRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={loading} size="lg" data-testid="onboarding-finish">
                Start 14-day Free Trial →
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Step1({ company, setCompany }) {
  return (
    <>
      <div className="font-heading font-bold text-2xl">Tell us about your business</div>
      <p className="text-sm text-muted-foreground mt-1">This information appears on your invoices.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Business Name *</Label><Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="ob-name" /></div>
        <div><Label>Owner Name</Label><Input value={company.owner_name || ""} onChange={(e) => setCompany({ ...company, owner_name: e.target.value })} /></div>
        <div><Label>Mobile</Label><Input value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></div>
        <div><Label>Business Email</Label><Input type="email" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} /></div>
        <div><Label>Logo URL</Label><Input value={company.logo_url || ""} onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} placeholder="https://…" /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
        <div><Label>City</Label><Input value={company.city || ""} onChange={(e) => setCompany({ ...company, city: e.target.value })} /></div>
        <div><Label>Pincode</Label><Input value={company.pincode || ""} onChange={(e) => setCompany({ ...company, pincode: e.target.value })} /></div>
      </div>
    </>
  );
}

function Step2({ company, setCompany }) {
  return (
    <>
      <div className="font-heading font-bold text-2xl">GST details</div>
      <p className="text-sm text-muted-foreground mt-1">Fill this only if your business is GST registered.</p>
      <div className="mt-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!company.gst_registered} onChange={(e) => setCompany({ ...company, gst_registered: e.target.checked })} data-testid="ob-gst-registered" />
          My business is GST registered
        </label>
        {company.gst_registered && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>GSTIN</Label><Input value={company.gst_number || ""} onChange={(e) => setCompany({ ...company, gst_number: e.target.value.toUpperCase() })} placeholder="27AAACU1234N1ZX" data-testid="ob-gstin" /></div>
            <div>
              <Label>State</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={company.state || ""} onChange={(e) => setCompany({ ...company, state: e.target.value })}>
                {STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>State Code</Label><Input value={company.state_code || ""} onChange={(e) => setCompany({ ...company, state_code: e.target.value })} placeholder="27" /></div>
            <div>
              <Label>Business Type</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={company.business_type || "Retail"} onChange={(e) => setCompany({ ...company, business_type: e.target.value })}>
                {["Retail","Wholesale","Manufacturer","Service","Contractor","Interior","Painting","Carpenter","Furniture","Electrical","Plumbing","Trader","Other"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Step3({ company, setCompany }) {
  return (
    <>
      <div className="font-heading font-bold text-2xl">Invoice settings</div>
      <p className="text-sm text-muted-foreground mt-1">Customize how your invoices look and are numbered.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div><Label>Invoice Prefix</Label><Input value={company.invoice_prefix || "INV"} onChange={(e) => setCompany({ ...company, invoice_prefix: e.target.value })} placeholder="INV" /></div>
        <div><Label>Starting Number</Label><Input type="number" value={company.invoice_start_number || 1} onChange={(e) => setCompany({ ...company, invoice_start_number: parseInt(e.target.value) || 1 })} /></div>
        <div className="sm:col-span-2 text-xs text-muted-foreground">
          Preview: <span className="font-mono">{company.invoice_prefix || "INV"}/26-27/{String(company.invoice_start_number || 1).padStart(4, "0")}</span>
        </div>
        <div className="sm:col-span-2"><Label>Terms &amp; Conditions</Label>
          <textarea className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm" rows={2} value={company.invoice_terms || ""} onChange={(e) => setCompany({ ...company, invoice_terms: e.target.value })} />
        </div>
        <div className="sm:col-span-2 pt-3 border-t"><div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Bank details (shown on invoices)</div></div>
        <div><Label>Bank Name</Label><Input value={company.bank_name || ""} onChange={(e) => setCompany({ ...company, bank_name: e.target.value })} /></div>
        <div><Label>Account Number</Label><Input value={company.account_number || ""} onChange={(e) => setCompany({ ...company, account_number: e.target.value })} /></div>
        <div><Label>IFSC</Label><Input value={company.ifsc || ""} onChange={(e) => setCompany({ ...company, ifsc: e.target.value })} /></div>
        <div><Label>UPI ID</Label><Input value={company.upi_id || ""} onChange={(e) => setCompany({ ...company, upi_id: e.target.value })} placeholder="yourname@bank" /></div>
      </div>
    </>
  );
}

function Step4({ company, onFinish, loading }) {
  return (
    <>
      <div className="font-heading font-bold text-2xl">You&apos;re almost there!</div>
      <p className="text-sm text-muted-foreground mt-1">Start your 14-day free trial. No credit card required.</p>
      <div className="mt-6 p-6 rounded-xl border-2 border-primary bg-primary/5">
        <div className="flex items-baseline gap-2">
          <div className="font-heading font-bold text-3xl">Free Trial</div>
          <div className="text-sm text-muted-foreground">— 14 days</div>
        </div>
        <div className="mt-2 text-sm text-muted-foreground">Full access to invoicing, quotations, customers, and PDF exports.</div>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-heading font-bold text-4xl">₹99</span><span className="text-sm text-muted-foreground">/month after trial</span>
        </div>
        <ul className="mt-5 space-y-1.5 text-sm">
          <li className="flex gap-2"><Check size={14} className="text-primary" /> Cancel anytime</li>
          <li className="flex gap-2"><Check size={14} className="text-primary" /> Your data stays yours forever</li>
          <li className="flex gap-2"><Check size={14} className="text-primary" /> WhatsApp &amp; PDF invoices included</li>
        </ul>
      </div>
    </>
  );
}
